import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

// Redux
import { connect } from 'react-redux';
import { setSchoolStatus, clearSchoolStatus } from '../redux/actions/statusActions';
import { logoutUser, checkAuthentication } from '../redux/actions/authActions';
import { checkSetupMode } from '../redux/actions/setupModeActions';
import { setDrawerOpen } from '../redux/actions/uiActions';
import { setTheme } from '../redux/actions/themeActions';

// Assets
import Logo from '../images/LogoWhiteBackground.png';

// Components
import NavDrawer from './NavDrawer';
import AttentionBell from './AttentionBell';
import ConfirmStatusModal from '../components/ConfirmStatusModal';
import AlertPicker from '../components/AlertPicker';

// Constants
import { AlertTypes, RED } from '../constants';
import { T, FONT_MONO } from '../constants/tokens';

/**
 * Below this the header drops its text buttons; the drawer has the same
 * actions. With them the row needs about 610px (logo, status, Set alert, Set
 * lockdown, three round buttons); without, about 350px, which fits a phone.
 */
const UTILS_WIDTH = 640;

/**
 * The v7 header: one 64px row of chrome, in a single-column shell.
 *
 * There is no rail. The design's `showRail` is false and `showCompactNav` is
 * true at every width, so the hamburger is always present and the drawer is
 * the only navigation surface. The header carries identity and the controls an
 * operator needs at all times: set lockdown, attention, theme. The theme
 * button stays at every width, since the drawer has no theme control.
 *
 * It no longer paints itself with the school status — that moved to
 * AlertBanner, which gets the full page width.
 */
class AppToolBar extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      showConfirmationModal: false,
      showAlertPicker: false,
      desiredStatus: null
    };
  }

  componentDidMount() {
    this.props.checkAuthentication(this.props.userID, this.props.isAuthenticated);
    if (this.props.isAuthenticated) this.props.checkSetupMode();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.userID !== this.props.userID || prevProps.isAuthenticated !== this.props.isAuthenticated) {
      this.props.checkAuthentication(this.props.userID, this.props.isAuthenticated);
      if (this.props.isAuthenticated) this.props.checkSetupMode();
    }
  }

  handleOpenConfirmationModal = (desiredStatus) => {
    this.setState({ showAlertPicker: false, showConfirmationModal: true, desiredStatus: desiredStatus });
  };

  handleOpenAlertPicker = () => {
    this.setState({ showAlertPicker: true });
  };

  handleCloseAlertPicker = () => {
    this.setState({ showAlertPicker: false });
  };

  handleCloseConfirmationModal = () => {
    this.setState({ showConfirmationModal: false });
  };

  confirmStatus = (status, isDrill = false) => {
    this.props.setSchoolStatus(status, isDrill);
    this.handleCloseConfirmationModal();
  }

  clearStatus = () => {
    this.props.clearSchoolStatus();
    this.handleCloseConfirmationModal();
  }

  render() {
    const { isAuthenticated, windowWidth, drawerOpen, openDrawer, themeMode, onSetTheme } = this.props;
    const status = this.props.schoolStatus || AlertTypes.UNKNOWN;
    const showUtils = windowWidth >= UTILS_WIDTH;
    const night = themeMode === 'night';

    const circleButton = {
      width: 36,
      height: 36,
      flex: 'none',
      borderRadius: 9999,
      border: '1px solid ' + T.hairline,
      background: T.elevated,
      color: T.ink,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      fontFamily: 'inherit',
      fontSize: 14
    };

    return (
      <>
        <AlertPicker
          open={this.state.showAlertPicker}
          onClose={this.handleCloseAlertPicker}
          onSelect={this.handleOpenConfirmationModal}
        />

        <ConfirmStatusModal
          closeModal={this.handleCloseConfirmationModal}
          showModal={this.state.showConfirmationModal}
          setStatus={this.confirmStatus}
          clearStatus={this.clearStatus}
          desiredStatus={this.state.desiredStatus}
          // By name: a rehydrated status is a fresh AlertType, not the constant.
          realAlertInForce={
            !this.props.isDrill &&
            !['Normal', 'All Clear', 'Unknown'].includes(status.getDashboardStatus())
          }
        />

        <header
          style={{
            height: 64,
            minWidth: 0,
            background: T.canvas,
            borderBottom: '1px solid ' + T.hairline,
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 12,
            padding: windowWidth < 480 ? '0 16px' : '0 20px',
            overflow: 'visible'
          }}
        >
          <Link to='/' style={{ flex: 'none', display: 'flex' }}>
            <img src={Logo} alt='SimulAlert' style={{ height: 32, width: 'auto', display: 'block' }} />
          </Link>

          {/* Status reads as a mono label here. The banner below carries the
              alarm, so the header stays legible chrome. */}
          {isAuthenticated &&
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flex: 'none',
                fontFamily: FONT_MONO,
                fontSize: 12,
                fontWeight: 500,
                textTransform: 'uppercase',
                color: T.muted,
                whiteSpace: 'nowrap'
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: status.getColor().main }} />
              {status.getDashboardStatus()}
            </span>}

          <div style={{ flex: '1 1 12px', minWidth: 0 }} />

          {isAuthenticated && showUtils &&
            <button
              type='button'
              onClick={this.handleOpenAlertPicker}
              style={{
                fontFamily: 'inherit',
                fontSize: 14,
                fontWeight: 500,
                lineHeight: '20px',
                height: 40,
                padding: '0 20px',
                borderRadius: 6,
                border: '1px solid ' + T.hairline,
                background: T.elevated,
                color: T.ink,
                cursor: 'pointer',
                flex: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              Set alert
            </button>}

          {isAuthenticated && showUtils &&
            <button
              type='button'
              onClick={() => this.handleOpenConfirmationModal(AlertTypes.EMERGENCY)}
              style={{
                fontFamily: 'inherit',
                fontSize: 14,
                fontWeight: 500,
                lineHeight: '20px',
                height: 40,
                padding: '0 20px',
                borderRadius: 6,
                border: 0,
                background: RED.main,
                color: RED.text,
                cursor: 'pointer',
                flex: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              Set lockdown
            </button>}

          {isAuthenticated && <AttentionBell />}

          {isAuthenticated &&
            <button
              type='button'
              aria-label={night ? 'Switch to day mode' : 'Switch to night mode'}
              onClick={() => onSetTheme(night ? 'day' : 'night')}
              style={circleButton}
            >
              <span aria-hidden>{night ? '☾' : '☀'}</span>
            </button>}

          {/* Always present — the drawer is the only navigation surface. */}
          {isAuthenticated &&
            <button
              type='button'
              aria-label='Open navigation'
              aria-expanded={drawerOpen}
              onClick={openDrawer}
              style={{ ...circleButton, flexDirection: 'column', gap: 3 }}
            >
              <span style={{ width: 14, height: 1.5, background: T.ink, display: 'block' }} />
              <span style={{ width: 14, height: 1.5, background: T.ink, display: 'block' }} />
              <span style={{ width: 14, height: 1.5, background: T.ink, display: 'block' }} />
            </button>}
        </header>

        {isAuthenticated &&
          <NavDrawer onSetStatus={this.handleOpenConfirmationModal} onOpenAlertPicker={this.handleOpenAlertPicker} />}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  schoolStatus: state.status.schoolStatus || AlertTypes.UNKNOWN,
  isDrill: state.status.isDrill === true,
  isLoadingSchoolStatus: state.status.isLoading,
  isAuthenticated: state.auth.isAuthenticated,
  userType: state.auth.userType,
  windowWidth: state.ui.windowSize.width,
  drawerOpen: state.ui.drawerOpen,
  themeMode: state.theme.mode
});

const mapDispatchToProps = dispatch => ({
  setSchoolStatus: (alertStatus, isDrill) => setSchoolStatus(dispatch, alertStatus, isDrill),
  clearSchoolStatus: () => clearSchoolStatus(dispatch),
  logout: () => {
    logoutUser(dispatch);
  },
  checkAuthentication: (userID, isAuthenticated) => checkAuthentication(dispatch, userID, isAuthenticated),
  checkSetupMode: () => checkSetupMode(dispatch),
  openDrawer: () => dispatch(setDrawerOpen(true)),
  onSetTheme: (mode) => dispatch(setTheme(mode))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AppToolBar));

AppToolBar.propTypes = {
  checkAuthentication: PropTypes.func.isRequired,
  checkSetupMode: PropTypes.func.isRequired,
  userID: PropTypes.number,
  isAuthenticated: PropTypes.bool,
  setSchoolStatus: PropTypes.func,
  isLoadingSchoolStatus: PropTypes.bool,
  clearSchoolStatus: PropTypes.func,
  schoolStatus: PropTypes.object,
  isDrill: PropTypes.bool,
  location: PropTypes.object,
  windowWidth: PropTypes.number,
  drawerOpen: PropTypes.bool,
  openDrawer: PropTypes.func,
  themeMode: PropTypes.string,
  onSetTheme: PropTypes.func,
  logout: PropTypes.func,
  userType: PropTypes.any
};
